import { useCallback, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { InformationalScreen, LoadingModal, Text } from '@adc/parallax-component-library';
import {
  Footer,
  FormContainer,
  InputPassword,
  MainContainer,
  PasswordInputValidator,
  TopHeaderBackArrow,
} from 'Components/utility';
import { ApiStatus } from 'Constants';
import { type FormikHelpers, useFormik } from 'formik';
import { usePasswordValidation } from 'Hooks';
import { SHOW_MODAL } from 'Reducers/modal/types';
import { setupPassword } from 'Services/verify';
import { type RootState } from 'src/reducers';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { store } from 'Utilities/store';

type MyFormValues = {
  newPassword: string;
  repeatNewPassword: string;
};

const initialValues: MyFormValues = {
  newPassword: '',
  repeatNewPassword: '',
};

const mapStateToProps = ({
  env: { deviceId, osType, osVersion, app, appVersion },
  config: { validation },
  auth: {
    include: { patient },
  },
  nav: { query },
}: RootState) => ({
  deviceId,
  osType,
  osVersion,
  app,
  appVersion,
  validation,
  patient,
  query,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const PasswordSetup = ({
  validation,
  deviceId,
  osType,
  osVersion,
  app,
  appVersion,
  patient,
  query,
}: Props) => {
  const [meetsPasswordRequirements, setMeetsPasswordRequirements] = useState(false);

  const isReset = query.reset === 'true';

  const onSubmit = useCallback(
    async (values: MyFormValues, { setFieldError }: FormikHelpers<MyFormValues>) => {
      const { data } = await setupPassword(
        values.newPassword,
        osType,
        osVersion,
        app,
        appVersion,
        deviceId
      );

      if (data?.code === ApiStatus.PASSWORD_VALIDATION) {
        if (data?.error === 'password.previous') {
          notifyApp('adc-webview:setup-password', { status: data.code, data });
          setFieldError(
            'newPassword',
            i18n.t('PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.previous')
          );

          return;
        }

        notifyApp('adc-webview:setup-password', { status: data.code, data });
        setFieldError(
          'newPassword',
          i18n.t('PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.invalid')
        );

        return;
      }

      notifyApp('adc-webview:setup-password', data);

      store.dispatch({
        type: SHOW_MODAL,
        modalProps: {
          testID: 'PasswordSetup.successModal',
          details: i18n.t('PasswordSetup.modals.passwordSetupSuccess.description'),
          onClose: () => {
            mediator.publish('router:redirect', '/login');
          },
        },
      });
    },
    [app, appVersion, deviceId, osType, osVersion]
  );

  const handleNavigateBack = useCallback(() => {
    mediator.publish('router:back');
    notifyApp('adc-webview:navigate-back-app');
  }, []);

  const validationSchema = usePasswordValidation({
    validateCurrentPassword: false,
    hasNewPassword: true,
    patient,
    translations: {
      newPassword: {
        required: i18n.t(
          'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.required'
        ),
        minLength: () => '',
        // PasswordInputValidator handles this error message
        // if needed, uncomment the following lines to return the error message
        // minLength: (values) =>
        //   i18n.t(
        //     'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.min',
        //     values
        //   ),
        maxLength: (values) =>
          i18n.t(
            'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.max',
            values
          ),
        trim: i18n.t('PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.trim'),
        repetitiveSequence: (values) =>
          i18n.t(
            'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.repetitiveSequence',
            values
          ),
        context: i18n.t(
          'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.context'
        ),
        dictionary: i18n.t(
          'PasswordSetup.content.passwordSetupForm.formField.newPassword.errors.dictionary'
        ),
        repeatRequired: i18n.t(
          'PasswordSetup.content.passwordSetupForm.formField.repeatNewPassword.errors.required'
        ),
        mustMatch: i18n.t(
          'PasswordSetup.content.passwordSetupForm.formField.repeatNewPassword.errors.passwordMustMatch'
        ),
      },
    },
  });

  const { handleSubmit, handleChange, handleBlur, isValid, errors, touched, values, isSubmitting } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit,
      validateOnBlur: true,
    });

  const isFormSubmittable = isValid && meetsPasswordRequirements;

  return (
    <MainContainer>
      {isSubmitting && <LoadingModal aria-label="Loading" />}

      <InformationalScreen
        topNavigationProps={{
          animatedTitle: true,
          title: isReset ? i18n.t('PasswordSetup.titleReset') : i18n.t('PasswordSetup.title'),
          buttonStart: {
            element: <TopHeaderBackArrow testID="Auth.action-go-back" />,
            label: isReset ? i18n.t('PasswordSetup.titleReset') : i18n.t('PasswordSetup.title'),
            onPress: handleNavigateBack,
          },
          testID: 'Auth.actionPasswordSetupTitle',
        }}
      >
        <InformationalScreen.Page>
          <InformationalScreen.Content>
            <Text marginTop={-8}>{i18n.t('PasswordSetup.subtitle')}</Text>
            <FormContainer hasHiddenUserInput>
              <PasswordInputValidator
                placeholder={i18n.t(
                  'PasswordSetup.content.passwordSetupForm.formField.newPassword.placeholder'
                )}
                label={i18n.t(
                  'PasswordSetup.content.passwordSetupForm.formField.newPassword.label'
                )}
                onChangeText={handleChange('newPassword')}
                errors={errors}
                touched={touched}
                handleBlur={handleBlur('newPassword')}
                password={values.newPassword}
                onValidate={setMeetsPasswordRequirements}
                identities={validation?.identity}
                testID="newPassword"
              />

              <InputPassword
                label={i18n.t(
                  'PasswordSetup.content.passwordSetupForm.formField.repeatNewPassword.label'
                )}
                placeholder={i18n.t(
                  'PasswordSetup.content.passwordSetupForm.formField.repeatNewPassword.placeholder'
                )}
                testID="repeatNewPassword"
                onBlur={handleBlur('repeatNewPassword')}
                onChangeText={handleChange('repeatNewPassword')}
                onSubmitEditing={() => {
                  if (isFormSubmittable) {
                    handleSubmit();
                  }
                }}
                defaultValue={values.repeatNewPassword}
                isInvalid={Boolean(errors.repeatNewPassword && touched.repeatNewPassword)}
                errorMessage={errors.repeatNewPassword}
              />
            </FormContainer>
          </InformationalScreen.Content>

          <InformationalScreen.Footer>
            <Footer
              buttonLabel={i18n.t('PasswordSetup.content.footerCreateAccount.submit.label')}
              onButtonPress={handleSubmit}
              buttonTestID="PasswordSetup.submit-btn"
              disabled={!isFormSubmittable}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(PasswordSetup);
